import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AudioLines, Play } from "lucide-react";
import { getConsent, isConsentTryAgain, postDecisions } from "../api/consent";
import { safeError } from "../lib/apiError";
import { toConsentLocale } from "../lib/consentLocale";
import { applyDeviceConsent } from "../lib/deviceConsent";
import logger from "../lib/logger";
import { useConsentStore } from "../store/consentStore";

export { useExternalMediaAllowed } from "../lib/externalMedia";

/** The site a hosted-player item loads from, as the placeholder names it. */
export function externalSiteName(provider: string | null | undefined, url: string): string {
  if (provider === "youtube") return "YouTube";
  if (provider === "soundcloud") return "SoundCloud";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * Records external_media ACCEPTED for this browser (source JUST_IN_TIME) and stores it: every placeholder on the page
 * turns into its player. Throws when nothing was recorded (the caller shows "try again").
 */
export async function alwaysAllowExternalMedia(language: string): Promise<void> {
  const overview = await getConsent(toConsentLocale(language));
  const topic = overview.topics.find((x) => x.key === "external_media");
  if (!topic) throw new Error("external_media is not published");
  await postDecisions([
    {
      versionId: topic.versionId,
      textId: topic.textId,
      decision: "ACCEPTED",
      source: "JUST_IN_TIME",
      clientAt: new Date().toISOString(),
    },
  ]);
  useConsentStore.getState().setDevice("external_media", {
    versionId: topic.versionId,
    decision: "ACCEPTED",
  });
  applyDeviceConsent();
}

/**
 * Click-to-load (Figma "Shared / Privacy / External media placeholder" 4420:1962, Kind=Video / Music): shown instead
 * of a YouTube / SoundCloud / hosted-player embed until this browser allows external media. Nothing from the other
 * site (nor the hosted player script) loads before a tap. Load = this one time, nothing recorded; Always allow =
 * external_media ACCEPTED.
 */
export default function ExternalMediaPlaceholder({
  site,
  music = false,
  onLoad,
}: {
  site: string;
  music?: boolean;
  onLoad: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<null | "retry" | "error">(null);
  const Icon = music ? AudioLines : Play;

  async function always() {
    setSaving(true);
    setFailed(null);
    try {
      await alwaysAllowExternalMedia(i18n.language);
      // The store flip replaces this placeholder with the player.
    } catch (e) {
      logger.warn("[Consent] always allow external media failed", safeError(e));
      setFailed(isConsentTryAgain(e) ? "retry" : "error");
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="external-media-placeholder"
      data-external-media={music ? "music" : "video"}
      className="flex h-full min-h-[160px] w-full flex-col overflow-hidden rounded-xl bg-[var(--bg-elevated)] text-[var(--foreground)]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-1 bg-[var(--bg-hover)] px-4 py-4 text-[var(--foreground-muted)]">
        <Icon size={music ? 24 : 32} aria-hidden="true" />
        <span className="text-sm font-semibold">{site}</span>
      </div>
      <div className="flex flex-col gap-3 px-3 py-3">
        <p className="m-0 text-sm">{t("consent_external_text", { site })}</p>
        {failed && (
          <p role="alert" data-testid="external-media-error" className="m-0 text-sm text-[var(--destructive-text)]">
            {failed === "retry" ? t("consent_try_again") : t("consent_error")}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="external-media-load"
            disabled={saving}
            onClick={onLoad}
            className="h-10 min-w-[120px] flex-1 rounded-full border border-[var(--border)] bg-[var(--bg-surface)] px-4 text-sm font-semibold text-[var(--foreground)] hover:bg-[var(--bg-hover)] disabled:opacity-40"
          >
            {t("consent_external_load")}
          </button>
          <button
            type="button"
            data-testid="external-media-always"
            disabled={saving}
            onClick={always}
            className="h-10 min-w-[120px] flex-1 rounded-full border border-[var(--border)] bg-[var(--bg-surface)] px-4 text-sm font-semibold text-[var(--foreground)] hover:bg-[var(--bg-hover)] disabled:opacity-40"
          >
            {t("consent_external_always")}
          </button>
        </div>
      </div>
    </div>
  );
}
